// Native-only auth helpers: Google Sign-In sheet + Face ID session.
// Every import is dynamic so web builds and old binaries degrade
// gracefully (callers treat any throw as "not available").
import { isNativeApp } from "@/lib/nativeBridge";

const IOS_CLIENT_ID = process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID;
const WEB_CLIENT_ID = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID;

let googleReady = false;

export function nativeGoogleAvailable() {
  return isNativeApp() && !!(IOS_CLIENT_ID || WEB_CLIENT_ID);
}

export async function nativeGoogleLogin() {
  const { SocialLogin } = await import("@capgo/capacitor-social-login");
  if (!googleReady) {
    await SocialLogin.initialize({
      google: {
        iOSClientId: IOS_CLIENT_ID,
        webClientId: WEB_CLIENT_ID,
        mode: "online",
      },
    });
    googleReady = true;
  }
  const res = await SocialLogin.login({ provider: "google", options: { scopes: ["email", "profile"] } });
  const idToken = res?.result?.idToken;
  if (!idToken) throw new Error("Google did not return a token");
  return idToken;
}

// ---- Face ID / biometric session -----------------------------------
const BIO_KEY = "ak9_bio_token";

export async function biometricsAvailable() {
  if (!isNativeApp()) return false;
  try {
    const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
    const info = await BiometricAuth.checkBiometry();
    return !!info.isAvailable;
  } catch {
    return false;
  }
}

// Human label per biometry type (plugin returns a numeric enum; string
// keys kept as a safety net across plugin versions).
const BIOMETRY_LABELS = {
  1: "Touch ID", touchId: "Touch ID",
  2: "Face ID", faceId: "Face ID",
  3: "fingerprint", fingerprintAuthentication: "fingerprint",
  4: "face unlock", faceAuthentication: "face unlock",
  5: "iris unlock", irisAuthentication: "iris unlock",
};

export async function biometryInfo() {
  if (!isNativeApp()) return { available: false, label: "biometrics" };
  try {
    const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
    const info = await BiometricAuth.checkBiometry();
    return {
      available: !!info.isAvailable,
      label: BIOMETRY_LABELS[info.biometryType] || "biometrics",
    };
  } catch {
    return { available: false, label: "biometrics" };
  }
}

export async function saveBiometricSession(token) {
  if (!isNativeApp() || !token) return;
  try {
    const { SecureStoragePlugin } = await import("capacitor-secure-storage-plugin");
    await SecureStoragePlugin.set({ key: BIO_KEY, value: token });
  } catch { /* keychain unavailable — non-fatal */ }
}

export async function hasBiometricSession() {
  if (!isNativeApp()) return false;
  try {
    const { SecureStoragePlugin } = await import("capacitor-secure-storage-plugin");
    const r = await SecureStoragePlugin.get({ key: BIO_KEY });
    return !!r?.value;
  } catch {
    return false;
  }
}

export async function biometricLogin() {
  const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
  await BiometricAuth.authenticate({
    reason: "Sign in to Armstrong K9 Registry",
    cancelTitle: "Use password instead",
    allowDeviceCredential: true,
  });
  const { SecureStoragePlugin } = await import("capacitor-secure-storage-plugin");
  const r = await SecureStoragePlugin.get({ key: BIO_KEY });
  if (!r?.value) throw new Error("No saved session");
  return r.value; // the stored JWT
}

export async function clearBiometricSession() {
  try {
    const { SecureStoragePlugin } = await import("capacitor-secure-storage-plugin");
    await SecureStoragePlugin.remove({ key: BIO_KEY });
  } catch { /* ignore */ }
}
